import { DateTimeFormat } from '@/models/common';
import { PreOrder } from '@/models/order';
import { STORAGE_KEY_POS_ORDERS_HISTORY, syncToServer } from '@/models/preOrder';
import { useLocalStorageState } from 'ahooks';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { useCallback, useEffect } from 'react';

export const useOrderHistory = (readonly: boolean) => {
  const [history, setHistory] = useLocalStorageState<Record<string, PreOrder>>(STORAGE_KEY_POS_ORDERS_HISTORY, {
    defaultValue: {},
  });

  useEffect(() => {
    try {
      // 只读模式不同步数据到服务器
      if (!readonly) {
        syncToServer(history);
      }
    } catch (e) {}
  }, [history, readonly]);

  const push = useCallback(
    (preOrder: PreOrder) => {
      const key = preOrder.key || new Date().getTime().toString(36);
      setHistory(prev => {
        return {
          ...prev,
          [key]: produce(preOrder, draft => {
            draft.key = draft.key || key;
            draft.addToStationTime = dayjs().format(DateTimeFormat);
          }),
        };
      });
    },
    [setHistory]
  );

  const pop = useCallback(
    (preOrder: PreOrder) => {
      setHistory(prev => {
        if (prev && prev[preOrder.key]) {
          const { [preOrder.key]: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    },
    [setHistory]
  );

  const popByOrderSn = useCallback(
    (orderSn: string) => {
      return pop(Object.values(history).find(v => v.orderSn === orderSn));
    },
    [history, pop]
  );

  const updateItem = useCallback(
    (preOrder: PreOrder) => {
      setHistory(prev => {
        if (prev && prev[preOrder.key]) {
          return { ...prev, [preOrder.key]: preOrder };
        }
        return prev;
      });
    },
    [setHistory]
  );

  const empty = useCallback(() => {
    setHistory({});
  }, [setHistory]);

  return { history, updateItem, push, pop, popByOrderSn, empty, size: history.length };
};
